import api from './axios';
import { ApiResponse, Gallery } from '../types';

// Transform backend response (snake_case) to frontend format (camelCase)
const transformGallery = (backendGallery: any): Gallery => {
  console.log('transformGallery - Input:', backendGallery);
  
  const transformed = {
    id: backendGallery._id || backendGallery.id,
    title: backendGallery.title,
    description: backendGallery.description,
    imageUrl: backendGallery.image_url || backendGallery.imageUrl,
    createdAt: backendGallery.createdAt || backendGallery.created_at,
    updatedAt: backendGallery.updatedAt || backendGallery.updated_at,
    createdBy: backendGallery.created_by || backendGallery.createdBy,
    updatedBy: backendGallery.updated_by || backendGallery.updatedBy,
    isActive: backendGallery.is_active !== undefined ? backendGallery.is_active : (backendGallery.isActive !== undefined ? backendGallery.isActive : true),
  };
  
  console.log('transformGallery - Output:', transformed);
  console.log('transformGallery - imageUrl:', transformed.imageUrl);
  
  return transformed;
};

const transformGalleries = (galleries: any[]): Gallery[] => {
  return galleries.map(transformGallery);
};

// Types for API responses
export type GalleryFilters = {
  page?: number;
  limit?: number;
  search?: string;
};

export type AdminGalleryFilters = GalleryFilters & {
  isActive?: boolean;
};

export type CreateGalleryPayload = {
  title: string;
  description?: string;
  image: File;
};

export type UpdateGalleryPayload = {
  title?: string;
  description?: string;
  image?: File;
};

// Public endpoints (authenticated users)

export const getGallery = async (
  filters: GalleryFilters = {}
): Promise<ApiResponse<{ galleries: Gallery[]; total: number; page: number; totalPages: number }>> => {
  const params: any = {
    page: filters.page || 1,
    limit: filters.limit || 12,
  };
  
  if (filters.search) {
    params.search = filters.search;
  }

  try {
    const response = await api.get<any>('/api/v1/gallery', { params });
    
    console.log('getGallery API Response (full):', response);
    console.log('getGallery API Response (data):', response.data);
    
    const responseData = response.data;
    let galleriesData: any[] = [];
    let total = 0;
    let page = filters.page || 1;
    let totalPages = 1;

    // Backend response structure: { success: true, data: { data: [...], pagination: {...} }, message: "..." }
    if (responseData && responseData.success !== false) {
      if (responseData.data) {
        // Images are in responseData.data.data (nested data property)
        if (responseData.data.data && Array.isArray(responseData.data.data)) {
          console.log('getGallery - Found images array in data.data, count:', responseData.data.data.length);
          galleriesData = responseData.data.data;
          
          // Pagination is in responseData.data.pagination
          if (responseData.data.pagination) {
            total = responseData.data.pagination.total || galleriesData.length;
            page = responseData.data.pagination.page || page;
            totalPages = responseData.data.pagination.total_pages || Math.ceil(total / (filters.limit || 12));
          } else {
            total = galleriesData.length;
            totalPages = Math.ceil(total / (filters.limit || 12));
          }
        } else if (Array.isArray(responseData.data)) {
          // Fallback: if data is directly an array
          console.log('getGallery - data is directly an array, count:', responseData.data.length);
          galleriesData = responseData.data;
          total = galleriesData.length;
          totalPages = Math.ceil(total / (filters.limit || 12));
        } else {
          console.warn('getGallery - Unexpected data structure:', responseData.data);
        }
      } else if (Array.isArray(responseData)) {
        // Fallback: if responseData is directly an array
        console.log('getGallery - responseData is directly an array, count:', responseData.length);
        galleriesData = responseData;
        total = galleriesData.length;
        totalPages = Math.ceil(total / (filters.limit || 12));
      }
    } else {
      console.warn('getGallery - Response indicates failure or no data:', responseData);
    }
    
    console.log('getGallery - Final galleriesData count:', galleriesData.length);
    console.log('getGallery - Pagination:', { total, page, totalPages });
    
    // Transform the data (backend returns mixed format: id is camelCase, but image_url, is_active are snake_case)
    const transformedGalleries = transformGalleries(galleriesData);
    console.log('getGallery - Transformed galleries count:', transformedGalleries.length);
    console.log('getGallery - First gallery:', transformedGalleries[0]);
    
    // Backend already filters for is_active: true, but keep client-side check as safety
    const activeGalleries = transformedGalleries.filter(g => g.isActive !== false);
    
    return {
      success: responseData?.success !== false,
      message: responseData?.message,
      data: {
        galleries: activeGalleries,
        total,
        page,
        totalPages,
      },
    };
  } catch (error: any) {
    console.error('getGallery - API Error:', error);
    console.error('getGallery - Error response:', error.response);
    console.error('getGallery - Error data:', error.response?.data);
    throw error;
  }
};

export const getGalleryImage = async (imageId: string): Promise<ApiResponse<Gallery>> => {
  const response = await api.get<any>(`/api/v1/gallery/${imageId}`);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message,
    data: transformGallery(response.data.data || response.data),
  };
};

// Admin endpoints

export const getAdminGallery = async (
  filters: AdminGalleryFilters = {}
): Promise<ApiResponse<{ galleries: Gallery[]; total: number; page: number; totalPages: number }>> => {
  const params: any = {
    page: filters.page || 1,
    limit: filters.limit || 10,
  };
  
  if (filters.isActive !== undefined) {
    params.isActive = filters.isActive;
  }
  if (filters.search) {
    params.search = filters.search;
  }

  const response = await api.get<any>('/api/v1/admin/gallery', { params });
  
  console.log('getAdminGallery API Response (full):', response);
  console.log('getAdminGallery API Response (data):', response.data);
  
  const responseData = response.data;
  let galleriesData: any[] = [];
  let total = 0;
  let page = filters.page || 1;
  let totalPages = 1;

  // Backend response structure: { success: true, data: { data: [...], pagination: {...} }, message: "..." }
  if (responseData && responseData.success !== false) {
    if (responseData.data) {
      // Images are in responseData.data.data (nested data property)
      if (responseData.data.data && Array.isArray(responseData.data.data)) {
        console.log('getAdminGallery - Found images array in data.data, count:', responseData.data.data.length);
        galleriesData = responseData.data.data;
        
        // Pagination is in responseData.data.pagination
        if (responseData.data.pagination) {
          total = responseData.data.pagination.total || galleriesData.length;
          page = responseData.data.pagination.page || page;
          totalPages = responseData.data.pagination.total_pages || Math.ceil(total / (filters.limit || 10));
        } else {
          total = galleriesData.length;
          totalPages = Math.ceil(total / (filters.limit || 10));
        }
      } else if (Array.isArray(responseData.data)) {
        // Fallback: if data is directly an array
        console.log('getAdminGallery - data is directly an array, count:', responseData.data.length);
        galleriesData = responseData.data;
        total = galleriesData.length;
        totalPages = Math.ceil(total / (filters.limit || 10));
      }
    } else if (Array.isArray(responseData)) {
      // Fallback: if responseData is directly an array
      console.log('getAdminGallery - responseData is directly an array, count:', responseData.length);
      galleriesData = responseData;
      total = galleriesData.length;
      totalPages = Math.ceil(total / (filters.limit || 10));
    }
  }
  
  console.log('getAdminGallery - Final galleriesData count:', galleriesData.length);
  console.log('getAdminGallery - Pagination:', { total, page, totalPages });
  
  return {
    success: responseData?.success !== false,
    message: responseData?.message,
    data: {
      galleries: transformGalleries(galleriesData),
      total,
      page,
      totalPages,
    },
  };
};

export const getAdminGalleryImage = async (imageId: string): Promise<ApiResponse<Gallery>> => {
  const response = await api.get<any>(`/api/v1/admin/gallery/${imageId}`);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message,
    data: transformGallery(response.data.data || response.data),
  };
};

export const createGalleryImage = async (payload: CreateGalleryPayload): Promise<ApiResponse<Gallery>> => {
  const formData = new FormData();
  formData.append('title', payload.title);
  if (payload.description) {
    formData.append('description', payload.description);
  }
  formData.append('image', payload.image);

  const response = await api.post<any>('/api/v1/admin/gallery', formData);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message || 'Gallery image created successfully',
    data: transformGallery(response.data.data || response.data),
  };
};

export const updateGalleryImage = async (
  imageId: string,
  payload: UpdateGalleryPayload
): Promise<ApiResponse<Gallery>> => {
  const formData = new FormData();
  
  if (payload.title !== undefined) {
    formData.append('title', payload.title);
  }
  if (payload.description !== undefined) {
    formData.append('description', payload.description);
  }
  if (payload.image) {
    formData.append('image', payload.image);
  }

  const response = await api.put<any>(`/api/v1/admin/gallery/${imageId}`, formData);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message || 'Gallery image updated successfully',
    data: transformGallery(response.data.data || response.data),
  };
};

export const deleteGalleryImage = async (imageId: string): Promise<ApiResponse<void>> => {
  const response = await api.delete<any>(`/api/v1/admin/gallery/${imageId}`);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message || 'Gallery image deleted successfully',
    data: undefined,
  };
};
